import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChapterAudioRecord } from "@repo/shared/chapterAudio";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Chapter } from "../../types";
import { tipTapJsonToPlainText, tryParseStringifiedTipTapDoc } from "../../../shared/tipTapPlainText";
import { userFacingError } from "../../../shared/userFacingError";
import {
  readChapterAudioVoiceId,
  type BookAudioSettings,
} from "../../../shared/bookAudioSettings";
import { cloudAuthDesktopV2 } from "../../services/cloudAuthDesktopV2";

const DEFAULT_ELEVENLABS_VOICE_ID = "JBFqnCBsd6RMkjVDRZzb";
const DEFAULT_LOCAL_VOICE_ID = "af_bella";
const DEFAULT_OPENAI_VOICE_ID = "nova";

type TtsVoice = { id: string; name: string; provider: "elevenlabs" | "openai" | "local" };
type BatchError = { title: string; message: string };

async function getToken(): Promise<string | null> {
  try {
    return await cloudAuthDesktopV2.getValidAccessToken();
  } catch {
    return null;
  }
}

function chapterPlainText(chapter: Chapter): string {
  const parsed = tryParseStringifiedTipTapDoc(chapter.content);
  return parsed ? tipTapJsonToPlainText(parsed) : chapter.content;
}

function pickDefaultVoiceId(voices: TtsVoice[]): string {
  return (
    voices.find((v) => v.provider === "elevenlabs" && v.id === DEFAULT_ELEVENLABS_VOICE_ID)?.id ??
    voices.find((v) => v.provider === "elevenlabs")?.id ??
    voices.find((v) => v.provider === "openai" && v.id === DEFAULT_OPENAI_VOICE_ID)?.id ??
    voices.find((v) => v.provider === "openai")?.id ??
    voices.find((v) => v.id === DEFAULT_LOCAL_VOICE_ID)?.id ??
    voices[0]?.id ??
    DEFAULT_ELEVENLABS_VOICE_ID
  );
}

function providerLabel(provider: TtsVoice["provider"]): string {
  if (provider === "local") return "Local";
  if (provider === "openai") return "OpenAI";
  return "ElevenLabs";
}

export function ChapterAudioControls({
  activeChapter,
  chapters,
  cloudEnabled,
  bookId,
  audioSettings,
  onChapterVoiceChange,
  onListen,
}: {
  activeChapter: Chapter;
  chapters: Chapter[];
  cloudEnabled: boolean;
  bookId: string | undefined;
  audioSettings: BookAudioSettings;
  onChapterVoiceChange: (chapterId: string, voiceId: string) => void;
  /** Called when Listen playback starts (after generate if needed). */
  onListen?: () => void;
}) {
  const [allVoices, setAllVoices] = useState<TtsVoice[]>([]);
  const [voicesReady, setVoicesReady] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [currentSource, setCurrentSource] = useState<"generated" | "upload" | null>(null);
  const [generating, setGenerating] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number; title: string } | null>(null);
  const [batchErrors, setBatchErrors] = useState<BatchError[]>([]);

  const ready = cloudEnabled && Boolean(bookId?.trim());
  const cancelBatchRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fallbackAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!ready) {
      setAllVoices([]);
      setVoicesReady(false);
      return;
    }
    let cancelled = false;
    void window.desktopV2Api.getChapterAudioTtsProvider().then((res) => {
      if (cancelled) return;
      if (!res.ok || !res.voices?.length) {
        setAllVoices([]);
        setVoicesReady(true);
        if (res.error) setError(res.error);
        return;
      }
      setAllVoices(
        res.voices.map((v) => ({
          id: v.id,
          name: v.name,
          provider: v.provider ?? "elevenlabs",
        }))
      );
      setVoicesReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [ready]);

  const bookVoices = useMemo(() => {
    const selected = audioSettings.voiceIds
      .map((id) => {
        const known = allVoices.find((v) => v.id === id);
        if (known) return known;
        // Custom ElevenLabs ids stored on the book but not in the catalog list.
        if (/^[a-zA-Z0-9]{16,64}$/.test(id) && !id.includes("_")) {
          const short =
            id.length > 12 ? `Custom (${id.slice(0, 6)}…${id.slice(-4)})` : `Custom (${id})`;
          return { id, name: short, provider: "elevenlabs" as const };
        }
        return null;
      })
      .filter((v): v is TtsVoice => Boolean(v));
    if (selected.length > 0) return selected;
    // No book selection yet — treat as single default voice.
    const fallbackId = pickDefaultVoiceId(allVoices);
    const fallback = allVoices.find((v) => v.id === fallbackId);
    return fallback ? [fallback] : [];
  }, [allVoices, audioSettings.voiceIds]);

  const multiVoice = bookVoices.length > 1;

  const voiceId = useMemo(() => {
    if (bookVoices.length === 0) return pickDefaultVoiceId(allVoices);
    if (!multiVoice) return bookVoices[0]!.id;
    const chapterVoice = readChapterAudioVoiceId(activeChapter.metadata.customFields);
    if (chapterVoice && bookVoices.some((v) => v.id === chapterVoice)) return chapterVoice;
    return bookVoices[0]!.id;
  }, [activeChapter.metadata.customFields, allVoices, bookVoices, multiVoice]);

  const onVoiceChange = useCallback(
    (id: string) => {
      onChapterVoiceChange(activeChapter.id, id);
    },
    [activeChapter.id, onChapterVoiceChange]
  );

  const loadStatus = useCallback(
    async (chapterId: string) => {
      if (!ready) {
        setCurrentUrl(null);
        setCurrentSource(null);
        return;
      }
      const token = await getToken();
      if (!token) {
        setCurrentUrl(null);
        setCurrentSource(null);
        return;
      }
      const res = await window.desktopV2Api.listChapterAudio({ chapterId, authToken: token });
      if (!res.ok) {
        setCurrentUrl(null);
        setCurrentSource(null);
        return;
      }
      const list = (res.audio ?? []) as ChapterAudioRecord[];
      const current = list.find((a) => a.isCurrent) ?? list[0];
      setCurrentUrl(current?.url ?? null);
      setCurrentSource(current?.source === "upload" ? "upload" : current ? "generated" : null);
    },
    [ready]
  );

  useEffect(() => {
    setError(null);
    setCopied(false);
    void loadStatus(activeChapter.id);
  }, [activeChapter.id, loadStatus]);

  const generateForChapter = useCallback(
    async (chapter: Chapter): Promise<ChapterAudioRecord> => {
      const token = await getToken();
      if (!token) throw new Error("Sign in to cloud to generate audio.");

      let chapterVoiceId = voiceId;
      if (multiVoice) {
        const stored = readChapterAudioVoiceId(chapter.metadata.customFields);
        if (stored && bookVoices.some((v) => v.id === stored)) chapterVoiceId = stored;
        else if (chapter.id === activeChapter.id) chapterVoiceId = voiceId;
        else chapterVoiceId = bookVoices[0]?.id ?? voiceId;
      }

      const res = await window.desktopV2Api.generateChapterAudio({
        chapterId: chapter.id,
        voiceId: chapterVoiceId,
        authToken: token,
        localText: chapterPlainText(chapter),
      });
      if (!res.ok || !res.audio) {
        throw new Error(res.error ?? "Audio generation failed.");
      }
      return res.audio as ChapterAudioRecord;
    },
    [activeChapter.id, bookVoices, multiVoice, voiceId]
  );

  const startPlayback = useCallback(
    (url: string) => {
      const inline = audioRef.current;
      if (inline) {
        if (inline.getAttribute("src") !== url) {
          inline.src = url;
        }
        void inline.play().catch(() => {
          /* autoplay may be blocked; controls remain available */
        });
      } else {
        fallbackAudioRef.current?.pause();
        const a = new Audio(url);
        fallbackAudioRef.current = a;
        void a.play().catch(() => {
          /* autoplay may be blocked */
        });
      }
      onListen?.();
    },
    [onListen]
  );

  const onListenClick = useCallback(async () => {
    if (generating || batchRunning || listening) return;
    setError(null);
    if (currentUrl) {
      startPlayback(currentUrl);
      return;
    }
    setListening(true);
    setGenerating(true);
    try {
      const audio = await generateForChapter(activeChapter);
      const url = audio.url || null;
      setCurrentUrl(url);
      setCurrentSource("generated");
      if (url) startPlayback(url);
    } catch (e) {
      setError(userFacingError(e, "generate"));
    } finally {
      setGenerating(false);
      setListening(false);
    }
  }, [
    activeChapter,
    batchRunning,
    currentUrl,
    generateForChapter,
    generating,
    listening,
    startPlayback,
  ]);

  const onGenerateActive = useCallback(async () => {
    if (generating || batchRunning) return;
    setError(null);
    setGenerating(true);
    try {
      const audio = await generateForChapter(activeChapter);
      setCurrentUrl(audio.url || null);
      setCurrentSource("generated");
    } catch (e) {
      setError(userFacingError(e, "generate"));
    } finally {
      setGenerating(false);
    }
  }, [activeChapter, batchRunning, generateForChapter, generating]);

  const onGenerateAll = useCallback(async () => {
    if (generating || batchRunning) return;
    const targets = [...chapters].sort((a, b) => a.order - b.order);
    if (targets.length === 0) return;
    setError(null);
    setBatchErrors([]);
    setBatchRunning(true);
    cancelBatchRef.current = false;
    const errors: BatchError[] = [];
    for (let i = 0; i < targets.length; i++) {
      if (cancelBatchRef.current) break;
      const chapter = targets[i];
      setBatchProgress({ done: i, total: targets.length, title: chapter.title });
      try {
        const audio = await generateForChapter(chapter);
        if (chapter.id === activeChapter.id) {
          setCurrentUrl(audio.url || null);
          setCurrentSource("generated");
        }
      } catch (e) {
        errors.push({
          title: `${chapter.order}. ${chapter.title}`,
          message: userFacingError(e, "generate"),
        });
      }
    }
    setBatchProgress(null);
    setBatchErrors(errors);
    setBatchRunning(false);
    cancelBatchRef.current = false;
  }, [activeChapter.id, batchRunning, chapters, generateForChapter, generating]);

  const onCopyUrl = useCallback(async () => {
    if (!currentUrl) return;
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable; ignore */
    }
  }, [currentUrl]);

  if (!ready) {
    return (
      <div className="flex flex-col gap-1.5" role="group" aria-label="Chapter audio">
        <span className="text-xs text-muted-foreground">
          Sign in to the cloud and enable cloud save to generate chapter audio.
        </span>
      </div>
    );
  }

  if (voicesReady && allVoices.length === 0) {
    return (
      <div className="flex flex-col gap-1.5" role="group" aria-label="Chapter audio">
        <span className="text-xs text-muted-foreground">
          {error ??
            "Chapter audio needs ELEVENLABS_API_KEY, OPENAI_API_KEY, or LOCAL_TTS_URL on the web server (apps/web/.env)."}
        </span>
      </div>
    );
  }

  if (!voicesReady || bookVoices.length === 0) {
    return (
      <div className="flex flex-col gap-1.5" role="group" aria-label="Chapter audio">
        <span className="text-xs text-muted-foreground">Loading voices…</span>
      </div>
    );
  }

  const busy = generating || batchRunning || listening;
  const activeVoice = bookVoices.find((v) => v.id === voiceId) ?? bookVoices[0]!;

  return (
    <div className="grid gap-4" role="group" aria-label="Chapter audio">
      <section className="grid gap-4 rounded-xl border border-border bg-card p-4 shadow-xs">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Current chapter</h4>
          <p className="mt-1 text-xs text-muted-foreground">
            Choose a voice, listen, or regenerate this chapter.
          </p>
        </div>

        {multiVoice ? (
          <label className="grid max-w-md gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Voice
            </span>
            <Select value={voiceId} onValueChange={onVoiceChange} disabled={busy}>
              <SelectTrigger className="w-full" aria-label="Voice for this chapter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {bookVoices.map((voice) => (
                  <SelectItem key={voice.id} value={voice.id}>
                    {voice.name} ({providerLabel(voice.provider)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        ) : (
          <p className="text-sm text-muted-foreground" title="Change the book default in Settings → Audio">
            Voice: <span className="font-medium text-foreground">{activeVoice.name}</span>{" "}
            ({providerLabel(activeVoice.provider)})
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => void onListenClick()} disabled={busy}>
            {listening || (generating && !currentUrl) ? "Preparing…" : "Listen"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void onGenerateActive()} disabled={busy}>
            {generating && !listening ? "Generating…" : currentUrl ? "Regenerate audio" : "Generate audio"}
          </Button>
          {currentUrl ? (
            <Badge variant="secondary" title={currentSource === "upload" ? "Human narration" : "Generated TTS"}>
              {currentSource === "upload" ? "Narration" : "Generated"}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">No audio yet</span>
          )}
        </div>

        {currentUrl ? (
          <div className="flex flex-wrap items-center gap-2">
            <audio
              ref={audioRef}
              key={currentUrl}
              className="h-9 min-w-64 max-w-full flex-1"
              controls
              preload="none"
              src={currentUrl}
            />
            <Button size="sm" variant="ghost" onClick={() => void onCopyUrl()} title={currentUrl}>
              {copied ? "Copied" : "Copy URL"}
            </Button>
          </div>
        ) : null}

        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </section>

      <section className="grid gap-3 rounded-xl border border-border bg-card p-4 shadow-xs">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Entire book</h4>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Generate audio for all {chapters.length} chapters in reading order.
          </p>
        </div>
        <div>
          <Button
            size="sm"
            variant={batchRunning ? "outline" : "secondary"}
            onClick={() => (batchRunning ? (cancelBatchRef.current = true) : void onGenerateAll())}
            disabled={generating || listening}
          >
            {batchRunning ? "Stop generating" : "Generate all chapters"}
          </Button>
        </div>
        {batchProgress ? (
          <div className="text-xs text-foreground/75" aria-live="polite">
            Generating {batchProgress.done + 1}/{batchProgress.total}: {batchProgress.title}
          </div>
        ) : null}
        {batchErrors.length > 0 ? (
          <div className="text-xs text-destructive" role="alert">
            <p>{batchErrors.length} chapter(s) failed:</p>
            <ul className="mt-1 list-disc pl-4">
              {batchErrors.map((item) => (
                <li key={item.title}>
                  {item.title} — {item.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
}
